// Organization members overview — the whole sequential "reload" flow of
// the Organization Members tab in one saga:
//
//   1. members + pending invitations, in parallel (a failure here is
//      user-visible: snackbar + empty lists);
//   2. manage-only extras — org workspaces (assignment dialog) and
//      policies (allowed-domain hint). 403s are expected for non-admin
//      members and stay silent.
//
// The intermediate put after step 1 keeps the loading spinner scoped to
// the members table, exactly like the old component-side reload().
import { call, put, select, all, takeLatest } from 'redux-saga/effects';
import axios from 'axios';
import { REQUEST, fetchSuccess, fetchFailure } from '../../actions/organizations/members';
import config from '../../../config';
import { getLocalUser } from '../../../utils/localUser';
import { Error as ErrorAction } from '../../actions/error/error';

const unwrap = (res) => (res && res.data && res.data.data !== undefined ? res.data.data : (res && res.data) || {});

export function* operation(action) {
  const { orgId } = action.payload;
  if (!orgId) return;
  const prev = (yield select((s) => s.organization_members?.response)) || {};

  let members = [];
  let invitations = [];
  try {
    const [mRes, iRes] = yield all([
      call(axios.get, `${config.API_URL}/organization/${orgId}/members`, { headers: getLocalUser() }),
      call(axios.get, `${config.API_URL}/organization/${orgId}/invitations?status=pending`, { headers: getLocalUser() }),
    ]);
    members = unwrap(mRes).members || [];
    invitations = unwrap(iRes).invitations || [];
    yield put(fetchSuccess({ ...prev, members, invitations }));
  } catch (err) {
    yield put(ErrorAction(err));
    const message = err?.response?.data?.message || 'Could not load members';
    yield put({ type: 'SHOW_ERROR_SNACKBAR', message });
    yield put(fetchSuccess({ ...prev, members: [], invitations: [] }));
    yield put(fetchFailure(message));
  }

  // Manage-only extras: org workspaces (assignment dialog) + policies
  // (domain hint). 403s are expected for non-admin members.
  let workspaces = [];
  try {
    const wRes = yield call(
      axios.get,
      `${config.API_URL}/organization/${orgId}/workspaces`,
      { headers: getLocalUser() },
    );
    workspaces = unwrap(wRes).workspaces || [];
  } catch (_err) { workspaces = []; }

  let allowedDomains = [];
  try {
    const oRes = yield call(
      axios.get,
      `${config.API_URL}/organization/${orgId}`,
      { headers: getLocalUser() },
    );
    allowedDomains = unwrap(oRes)?.policies?.allowed_email_domains || [];
  } catch (_err) { allowedDomains = []; }

  yield put(fetchSuccess({ members, invitations, workspaces, allowedDomains }));
}

export function* watch() {
  yield takeLatest(REQUEST, operation);
}
